import { rawToken, token, type ClacColorToken } from './uiTokens';

/**
 * Fuente única de tokens de diseño para los componentes.
 *
 * Los valores de color no son literales: apuntan a variables CSS cuyo valor por modo
 * vive en `uiTokens.ts`. Por eso un componente puede escribir `ui.semantic.bg.surface`
 * sin saber nada del modo activo — el navegador resuelve el color en tiempo de pintado.
 *
 * Lo que NO es color (medidas, radios, tipografía) sigue siendo literal, porque no
 * cambia entre modos.
 */
function buildTokens(resolve: (name: ClacColorToken) => string) {
  const c = resolve;
  return {
    colors: {
      white: c('color-white'),
      black: c('color-black'),
      gray: {
        50: c('gray-50'),
        100: c('gray-100'),
        200: c('gray-200'),
        300: c('gray-300'),
        400: c('gray-400'),
        600: c('gray-600'),
        950: c('gray-950')
      },
      blue: {
        50: c('blue-50'),
        100: c('blue-100'),
        200: c('blue-200'),
        400: c('blue-400'),
        600: c('blue-600'),
        800: c('blue-800'),
        950: c('blue-950')
      },
      green: {
        50: c('green-50'),
        100: c('green-100'),
        200: c('green-200'),
        400: c('green-400'),
        600: c('green-600'),
        800: c('green-800'),
        950: c('green-950')
      },
      red: {
        50: c('red-50'),
        100: c('red-100'),
        200: c('red-200'),
        400: c('red-400'),
        600: c('red-600'),
        800: c('red-800')
      },
      amber: {
        50: c('amber-50'),
        100: c('amber-100'),
        200: c('amber-200'),
        400: c('amber-400'),
        700: c('amber-700')
      },
      purple: {
        50: c('purple-50'),
        200: c('purple-200'),
        700: c('purple-700'),
        900: c('purple-900')
      }
    },

    semantic: {
      bg: {
        page: c('bg-page'),
        surface: c('bg-surface'),
        scrim: c('bg-scrim')
      },
      text: {
        primary: c('text-primary'),
        secondary: c('text-secondary'),
        muted: c('text-muted')
      },
      border: {
        subtle: c('border-subtle'),
        default: c('border-default'),
        emphasis: c('border-emphasis'),
        focus: c('border-focus'),
        light: c('border-light')
      },
      accent: {
        main: c('accent-main'),
        hover: c('accent-hover'),
        bg: c('accent-bg'),
        bgHover: c('accent-bg-hover'),
        border: c('accent-border'),
        text: c('accent-text'),
        focusShadow: c('accent-focus-shadow')
      },
      brand: {
        bg: c('brand-bg'),
        border: c('brand-border'),
        dark: c('brand-dark'),
        primary: c('brand-primary'),
        markBg: c('brand-mark-bg')
      },
      danger: {
        bg: c('danger-bg'),
        border: c('danger-border'),
        text: c('danger-text'),
        icon: c('danger-icon'),
        focusShadow: c('danger-focus-shadow'),
        // Caja roja siempre oscura, con significado propio en los dos modos.
        dark: {
          bg: '#2D1A1A',
          text: '#F87171',
          border: '#7F1D1D'
        }
      },
      warning: {
        bg: c('warning-bg'),
        border: c('warning-border'),
        text: c('warning-text')
      },
      success: {
        bg: c('success-bg'),
        border: c('success-border'),
        text: c('success-text')
      },
      info: {
        bg: c('info-bg'),
        text: c('info-text')
      },
      n: {
        50: c('n-50'),
        100: c('n-100'),
        200: c('n-200'),
        300: c('n-300'),
        400: c('n-400'),
        600: c('n-600'),
        800: c('n-800'),
        950: c('n-950'),
        // Chip/toast siempre oscuro, independiente del modo (mismo criterio que danger.dark).
        dark: {
          bg: '#1E2229',
          text: '#F4F5F7'
        }
      }
    },

    shadow: {
      card: c('shadow-card'),
      raised: c('shadow-raised'),
      modal: c('shadow-modal')
    },

    layout: {
      sidebarWidth: 248,
      sidebarWidthMini: 72,
      topbarHeight: 60,
      brandHeight: 73, // shell .sidebar-brand min-height
      subNavHeight: 44
    },

    radius: {
      sm: '6px',
      md: '10px',
      lg: '14px',
      xl: '20px',
      pill: '100px'
    },

    fonts: {
      sizes: {
        '4xs': '0.5625rem',
        '3xs': '0.625rem',
        '2xs': '0.6875rem',
        xs: '.75rem',
        sm: '0.8125rem',
        md: '0.875rem',
        lg: '1rem',
        lg2: '1.125rem', // 18px — shell --text-18
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.75rem'
      },
      families: {
        heading: "'Plus Jakarta Sans', sans-serif",
        body: "'Inter var', 'Inter', sans-serif" // variable local + fallback estático (fontsource)
      },
      weights: {
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
        extrabold: 800
      }
    }
  } as const;
}

/**
 * Tokens para la interfaz: cada color es una variable CSS, así que cambia solo con el
 * modo activo sin volver a renderizar.
 */
export const clacUiGlobal = buildTokens(token);

/**
 * Mismos tokens con valores literales de modo claro. Se usa donde una variable CSS no
 * se resuelve: reportes PDF (`@react-pdf/renderer`) y dibujo en canvas.
 */
export const clacUiGlobalRaw = buildTokens((name) => rawToken(name, 'light'));

export type ClacUiGlobalColors = typeof clacUiGlobal.colors;
export type ClacUiGlobalSemantic = typeof clacUiGlobal.semantic;
export type ClacUiGlobalFonts = typeof clacUiGlobal.fonts;
export type ClacUiGlobal = typeof clacUiGlobal;
